import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Modal, ProgressBar, Card, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { RequiredFile } from '../../types';
import { formatTimestamp } from '../../utils/dateFormatters';
import { fetchStatementGaps } from '../../api/cases';
import { useAuth } from '../../contexts/AuthContext';

interface RequiredFilesListProps {
  case_id: string;
  requirements: RequiredFile[];
  onCreateRequirement: () => void;
  onEditRequirement: (requirement: RequiredFile) => void;
  onDeleteRequirement: (requirement_id: string) => Promise<void>;
  onRefresh: () => Promise<void>;
  onUnmatchFile?: (requirement_id: string, file_id: string, month?: string) => Promise<void>;
  caseFiles?: any[]; // CaseFile[] but avoiding circular import
}

interface StatementGapFile {
  file_id: string;
  filename: string;
  statement_date: string;
}

interface StatementGapAccount {
  bank_name: string;
  account_number: string;
  bank_name_variants_merged: string[];
  statement_count: number;
  covered_range: { from: string; to: string };
  missing_months: string[];
  balance_gaps: Array<{
    statement1: { statement_id: string; date: string; ending_balance: string | number | null; file_id: string; filename: string };
    statement2: { statement_id: string; date: string; beginning_balance: string | number | null; file_id: string; filename: string };
    difference: number;
  }>;
  files: StatementGapFile[];
}

const getStatusBadgeVariant = (status: RequiredFile['status']) => {
  switch (status) {
    case 'pending': return 'warning';
    case 'partial': return 'info';
    case 'complete': return 'success';
    default: return 'secondary';
  }
};

const RequiredFilesList: React.FC<RequiredFilesListProps> = ({
  case_id,
  requirements,
  onCreateRequirement,
  onEditRequirement,
  onDeleteRequirement,
  onRefresh,
  onUnmatchFile,
  caseFiles = []
}) => {
  const auth = useAuth();
  const token = auth.user?.id_token;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [requirementToDelete, setRequirementToDelete] = useState<RequiredFile | null>(null);

  const [gapAccounts, setGapAccounts] = useState<StatementGapAccount[]>([]);
  const [gapsLoading, setGapsLoading] = useState(true);
  const [gapsError, setGapsError] = useState<string | null>(null);
  const [expandedFilesFor, setExpandedFilesFor] = useState<Set<string>>(new Set());

  const toggleFilesExpanded = (accountKey: string) => {
    setExpandedFilesFor(prev => {
      const next = new Set(prev);
      if (next.has(accountKey)) next.delete(accountKey); else next.add(accountKey);
      return next;
    });
  };

  useEffect(() => {
    setGapsLoading(true);
    fetchStatementGaps(case_id, token)
      .then(({ accounts }) => setGapAccounts(accounts))
      .catch(e => setGapsError(e?.message ?? 'Failed to load statement coverage gaps'))
      .finally(() => setGapsLoading(false));
  }, [case_id, token]);

  const handleDelete = async () => {
    if (requirementToDelete) {
      await onDeleteRequirement(requirementToDelete.requirement_id);
      setShowDeleteConfirm(false);
      setRequirementToDelete(null);
    }
  };

  const getProgressPercentage = (requirement: RequiredFile): number => {
    if (requirement.file_type === 'bank_statement' && requirement.required_months) {
      const satisfied = requirement.required_months.filter(m => m.status === 'satisfied').length;
      const total = requirement.required_months.length;
      return total > 0 ? Math.round((satisfied / total) * 100) : 0;
    } else if (requirement.required_count && requirement.matched_files) {
      const matched = requirement.matched_files.length;
      const required = requirement.required_count;
      return required > 0 ? Math.round((matched / required) * 100) : 0;
    }
    return 0;
  };

  const renderProgress = (requirement: RequiredFile) => {
    if (requirement.file_type === 'bank_statement' && requirement.required_months) {
      const satisfied = requirement.required_months.filter(m => m.status === 'satisfied').length;
      const total = requirement.required_months.length;
      const pending = requirement.required_months.filter(m => m.status === 'pending');
      
      return (
        <div>
          <ProgressBar 
            now={getProgressPercentage(requirement)} 
            variant={requirement.status === 'complete' ? 'success' : requirement.status === 'partial' ? 'info' : 'warning'}
            className="mb-1"
          />
          <small className="text-muted">{satisfied} of {total} months satisfied</small>
          {pending.length > 0 && (
            <div className="mt-1">
              <small className="text-warning">
                Missing: {pending.map(m => m.month).join(', ')}
              </small>
            </div>
          )}
        </div>
      );
    } else if (requirement.required_count && requirement.matched_files) {
      const matched = requirement.matched_files.length;
      const required = requirement.required_count;
      const remaining = required - matched;
      
      return (
        <div>
          <ProgressBar 
            now={getProgressPercentage(requirement)} 
            variant={requirement.status === 'complete' ? 'success' : requirement.status === 'partial' ? 'info' : 'warning'}
            className="mb-1"
          />
          <small className="text-muted">{matched} of {required} files matched</small>
          {remaining > 0 && (
            <div className="mt-1">
              <small className="text-warning">
                {remaining} more file{remaining !== 1 ? 's' : ''} needed
              </small>
            </div>
          )}
        </div>
      );
    }
    return <span className="text-muted">No progress tracking</span>;
  };

  const renderDetails = (requirement: RequiredFile) => {
    const details: string[] = [];
    
    if (requirement.date_range) {
      details.push(`Date Range: ${requirement.date_range.from} to ${requirement.date_range.to}`);
    }
    
    if (requirement.account_filter) {
      const filterParts: string[] = [];
      if (requirement.account_filter.bank_name) {
        filterParts.push(`Bank: ${requirement.account_filter.bank_name}`);
      }
      if (requirement.account_filter.account_number) {
        filterParts.push(`Account: ${requirement.account_filter.account_number}`);
      }
      if (filterParts.length > 0) {
        details.push(filterParts.join(', '));
      }
    }
    
    if (requirement.notes) {
      details.push(`Notes: ${requirement.notes}`);
    }
    
    return details.length > 0 ? (
      <div className="mt-2">
        {details.map((detail, idx) => (
          <small key={idx} className="text-muted d-block">{detail}</small>
        ))}
      </div>
    ) : null;
  };

  const getFileName = (file_id: string): string => {
    const file = caseFiles.find(f => f.file_id === file_id);
    return file?.filename || file_id;
  };

  const renderMatchedItems = (requirement: RequiredFile) => {
    // For bank statements, show matched months
    if (requirement.file_type === 'bank_statement' && requirement.required_months) {
      const satisfiedMonths = requirement.required_months.filter(m => m.status === 'satisfied');
      if (satisfiedMonths.length === 0) return null;

      return (
        <div className="mt-3">
          <small className="text-muted d-block mb-2"><strong>Matched Months:</strong></small>
          <div className="d-flex flex-wrap gap-2">
            {satisfiedMonths.map((month) => (
              <Badge key={month.month} bg="success" className="d-inline-flex align-items-center">
                {month.month}
                {month.file_id && (
                  <span className="ms-2">({getFileName(month.file_id)})</span>
                )}
                {onUnmatchFile && month.file_id && (
                  <Button
                    variant="link"
                    size="sm"
                    className="text-white p-0 ms-2"
                    style={{ fontSize: '0.75rem', lineHeight: 1 }}
                    onClick={async () => {
                      if (onUnmatchFile && month.file_id) {
                        await onUnmatchFile(requirement.requirement_id, month.file_id, month.month);
                        await onRefresh();
                      }
                    }}
                  >
                    ×
                  </Button>
                )}
              </Badge>
            ))}
          </div>
        </div>
      );
    }
    
    // For other file types, show matched files
    if (requirement.matched_files && requirement.matched_files.length > 0) {
      return (
        <div className="mt-3">
          <small className="text-muted d-block mb-2"><strong>Matched Files:</strong></small>
          <div className="d-flex flex-column gap-1">
            {requirement.matched_files.map((matchedFile) => (
              <div key={matchedFile.file_id} className="d-flex align-items-center justify-content-between">
                <span>{getFileName(matchedFile.file_id)}</span>
                {onUnmatchFile && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={async () => {
                      if (onUnmatchFile) {
                        await onUnmatchFile(requirement.requirement_id, matchedFile.file_id);
                        await onRefresh();
                      }
                    }}
                  >
                    Unmatch
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return null;
  };

  const accountsWithGaps = gapAccounts.filter(a => a.missing_months.length > 0 || a.balance_gaps.length > 0);

  return (
    <div>
      <div className="mt-3 mb-4">
        <h3>Bank Statement Coverage Gaps</h3>
        {gapsLoading ? (
          <Spinner animation="border" size="sm" />
        ) : gapsError ? (
          <Alert variant="warning" className="py-2">{gapsError}</Alert>
        ) : gapAccounts.length === 0 ? (
          <p className="text-muted">No bank statements on file yet for this case.</p>
        ) : accountsWithGaps.length === 0 ? (
          <Alert variant="success" className="py-2 mb-0">
            No gaps detected across {gapAccounts.length} account{gapAccounts.length !== 1 ? 's' : ''} on file.
          </Alert>
        ) : (
          <div className="row">
            {accountsWithGaps.map(account => {
              const accountKey = `${account.bank_name}-${account.account_number}`;
              const filesExpanded = expandedFilesFor.has(accountKey);
              return (
                <div key={accountKey} className="col-md-6 mb-3">
                  <Card border="warning">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <strong>{account.bank_name} (...{account.account_number.slice(-4)})</strong>
                      <Badge bg="warning" text="dark">
                        {account.missing_months.length} missing month{account.missing_months.length !== 1 ? 's' : ''}
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      <small className="text-muted d-block mb-1">
                        {account.statement_count} statement{account.statement_count !== 1 ? 's' : ''} on file,
                        {' '}covering {account.covered_range.from} to {account.covered_range.to}
                      </small>
                      {account.bank_name_variants_merged.length > 1 && (
                        <small className="text-muted d-block mb-2 fst-italic">
                          Combined from bank-name variants seen across statements: {account.bank_name_variants_merged.join(', ')}
                        </small>
                      )}
                      {account.missing_months.length > 0 && (
                        <div className="mb-2">
                          <small className="text-warning">
                            Missing: {account.missing_months.join(', ')}
                          </small>
                        </div>
                      )}
                      {account.balance_gaps.length > 0 && (
                        <div className="mb-2">
                          <small className="text-danger d-block mb-1">
                            <strong>Balance doesn't carry over:</strong>
                          </small>
                          {account.balance_gaps.map((gap, idx) => (
                            <small key={idx} className="text-danger d-block">
                              <Link to={`/case/${case_id}/file/${gap.statement1.file_id}`} target="_blank" rel="noopener noreferrer">
                                {gap.statement1.date} ending {gap.statement1.ending_balance}
                              </Link>
                              {' → '}
                              <Link to={`/case/${case_id}/file/${gap.statement2.file_id}`} target="_blank" rel="noopener noreferrer">
                                {gap.statement2.date} beginning {gap.statement2.beginning_balance}
                              </Link>
                            </small>
                          ))}
                        </div>
                      )}
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={() => toggleFilesExpanded(accountKey)}
                      >
                        {filesExpanded ? 'Hide' : 'Show'} {account.files.length} source file{account.files.length !== 1 ? 's' : ''}
                      </Button>
                      {filesExpanded && (
                        <div className="mt-2">
                          {account.files.map(f => (
                            <div key={f.file_id} className="small">
                              <Link to={`/case/${case_id}/file/${f.file_id}`} target="_blank" rel="noopener noreferrer">
                                {f.statement_date}: {f.filename}
                              </Link>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <h3>Required Files</h3>
        <Button variant="primary" onClick={onCreateRequirement}>
          Add Requirement
        </Button>
      </div>
      
      {requirements.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-muted mb-0">No required files defined yet.</p>
            <Button variant="primary" size="sm" className="mt-3" onClick={onCreateRequirement}>
              Add First Requirement
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div className="row">
          {requirements.map((requirement) => (
            <div key={requirement.requirement_id} className="col-md-6 mb-3">
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{requirement.description}</strong>
                    <Badge bg={getStatusBadgeVariant(requirement.status)} className="ms-2">
                      {requirement.status}
                    </Badge>
                  </div>
                  <div>
                    <Button 
                      variant="link" 
                      size="sm"
                      className="text-primary p-0 me-2"
                      onClick={() => onEditRequirement(requirement)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="link" 
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => {
                        setRequirementToDelete(requirement);
                        setShowDeleteConfirm(true);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <small className="text-muted">Category:</small> {requirement.category}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">File Type:</small> {requirement.file_type}
                  </div>
                  {renderProgress(requirement)}
                  {renderDetails(requirement)}
                  {renderMatchedItems(requirement)}
                  <div className="mt-2">
                    <small className="text-muted">
                      Created: {formatTimestamp(requirement.created_at)} by {requirement.created_by}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the requirement "{requirementToDelete?.description}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RequiredFilesList;

